import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Drawer, Dropdown, Grid, Layout, Menu, Typography } from 'antd';
import {
  AppstoreOutlined,
  ExportOutlined,
  FileTextOutlined,
  HomeOutlined,
  ImportOutlined,
  LogoutOutlined,
  MenuOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { LoginContext } from '../../context/loginContext';
import { ADMIN_ROLES } from '../../constants/roles';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

const allMenuItems = [
  { key: '/administrador/home', icon: <HomeOutlined />, label: 'Inicio' },
  { key: '/administrador/clientes', icon: <TeamOutlined />, label: 'Clientes' },
  { key: '/administrador/productos', icon: <AppstoreOutlined />, label: 'Productos' },
  { key: '/administrador/conceptos', icon: <FileTextOutlined />, label: 'Conceptos' },
  { key: '/administrador/exportacion', icon: <ExportOutlined />, label: 'Exportacion' },
  { key: '/administrador/importacion', icon: <ImportOutlined />, label: 'Importación' },
  { key: '/administrador/usuarios', icon: <UserOutlined />, label: 'Usuarios', adminOnly: true },
];

const MainLayout = ({ children }) => {
  const { user, logout } = useContext(LoginContext);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const role = String(user?.tiu_descripcion || '').toUpperCase();
  const isAdmin = ADMIN_ROLES.includes(role);
  const menuKeys = allMenuItems
    .filter((item) => !item.adminOnly || isAdmin)
    .map(({ key, icon, label }) => ({ key, icon, label }));

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Limpieza local aunque falle backend
    }
    logout();
    navigate('/login', { replace: true });
  };

  const onMenuNavigate = ({ key }) => {
    navigate(key);
    setDrawerOpen(false);
  };

  const menuNode = (
    <>
      <div
        style={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingInline: 12,
          borderBottom: '1px solid #d8e8dc',
        }}
      >
        <Text style={{ color: '#198039', fontSize: 14, fontWeight: 700 }}>Global Admin</Text>
      </div>
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuKeys}
        onClick={onMenuNavigate}
        style={{ marginTop: 4, borderRight: 0, fontSize: 12 }}
      />
    </>
  );

  return (
    <>
      <Layout style={{ minHeight: '100vh' }}>
        {!isMobile && (
          <Sider
            theme="light"
            width={190}
            style={{ background: '#ffffff', minHeight: '100vh', borderRight: '1px solid #d8e8dc' }}
          >
            {menuNode}
          </Sider>
        )}
        <Layout style={{ minWidth: 0 }}>
          <Header
            style={{
              background: '#ffffff',
              borderBottom: '1px solid #d8e8dc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: 48,
              lineHeight: '48px',
              paddingInline: isMobile ? 8 : 10,
              gap: 8,
              position: 'sticky',
              top: 0,
              zIndex: 100,
            }}
          >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: 18 }} />}
                onClick={() => setDrawerOpen(true)}
                aria-label="Abrir menu"
              />
            )}
            <Text
              ellipsis
              style={{ color: '#198039', fontSize: 12, flex: isMobile ? 1 : undefined }}
            >
              {role || 'USUARIO'}
            </Text>
          </div>
          <Dropdown
            menu={{
              items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'Cerrar sesion', danger: true }],
              onClick: ({ key }) => key === 'logout' && handleLogout(),
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              style={{
                color: '#166534',
                fontSize: 12,
                paddingInline: 6,
                maxWidth: isMobile ? 140 : undefined,
              }}
            >
              <Avatar size={20} icon={<UserOutlined />} />
              <span style={{ marginLeft: 6, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.usu_usuario || 'Usuario'}
              </span>
            </Button>
          </Dropdown>
        </Header>
        <Content
          style={{
            padding: isMobile ? '8px 8px 16px' : 12,
            background: '#f3faf6',
            minHeight: 'calc(100vh - 48px)',
          }}
        >
          {children}
        </Content>
        </Layout>
      </Layout>

      <Drawer
        title={null}
        placement="left"
        width={280}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        styles={{ body: { padding: 0 } }}
        destroyOnClose={false}
      >
        {menuNode}
      </Drawer>
    </>
  );
};

export default MainLayout;
